import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { LayoutDashboard, ShoppingCart, PlusCircle, Smartphone, Menu, PanelLeftOpen } from 'lucide-react';
import ChangePasswordModal from './ChangePasswordModal';
import CaisseFermetureReminder from './caisse/CaisseFermetureReminder';
import ChatSidebar from './ChatSidebar';
import OfflineStatusBar from './OfflineStatusBar';
import CommercialModeBanner from './CommercialModeBanner';
import AppHeader from './layout/AppHeader';
import StaffPrimaryNav from './layout/StaffPrimaryNav';
import StaffPlusDrawer from './layout/StaffPlusDrawer';
import GlobalSearchPalette from './layout/GlobalSearchPalette';
import { getStaffHomeRoute, resolveMagasinRole } from '../utils/roleRouting';
import { SITE } from '../utils/siteConfig';

const NAV_COLLAPSED_KEY = 'staff_nav_collapsed';

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [navCollapsed, setNavCollapsed] = useState(() => {
    try { return localStorage.getItem(NAV_COLLAPSED_KEY) === '1'; } catch { return false; }
  });
  const [plusOpen, setPlusOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const { user, logout, api } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const homeRoute = getStaffHomeRoute(user);
  const isTech = resolveMagasinRole(user) === 'technicien';

  useEffect(() => {
    const openSearch = () => setSearchOpen(true);
    window.addEventListener('rm-open-search', openSearch);
    return () => window.removeEventListener('rm-open-search', openSearch);
  }, []);

  const handleLogout = () => { logout(); navigate('/'); };
  const isActive = (path) => location.pathname === path || location.pathname.startsWith(`${path}/`);

  const toggleNavCollapsed = useCallback(() => {
    setNavCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem(NAV_COLLAPSED_KEY, next ? '1' : '0'); } catch { /* ignore */ }
      if (!next) setSidebarOpen(false);
      return next;
    });
  }, []);

  const handleMenuToggle = () => {
    if (typeof window !== 'undefined' && window.innerWidth >= 768 && navCollapsed) {
      toggleNavCollapsed();
      return;
    }
    setSidebarOpen((v) => !v);
  };

  return (
    <div className="min-h-screen">
      <div className="grid-pattern" />

      <AppHeader
        menuOpen={sidebarOpen}
        onMenuToggle={handleMenuToggle}
        navCollapsed={navCollapsed}
        onNavCollapseToggle={toggleNavCollapsed}
        onOpenSearch={() => setSearchOpen(true)}
        onOpenPlus={() => setPlusOpen(true)}
      />

      <CommercialModeBanner />
      <OfflineStatusBar />

      <StaffPrimaryNav
        sidebarOpen={sidebarOpen}
        desktopCollapsed={navCollapsed}
        onClose={() => setSidebarOpen(false)}
        onCollapse={toggleNavCollapsed}
        onLogout={handleLogout}
        onChangePassword={() => setShowChangePassword(true)}
      />

      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/55 z-[35] top-16 md:hidden" onClick={() => setSidebarOpen(false)} data-testid="sidebar-overlay" />
      )}

      <StaffPlusDrawer open={plusOpen} onClose={() => setPlusOpen(false)} />
      <GlobalSearchPalette open={searchOpen} onClose={() => setSearchOpen(false)} api={api} />

      <main className={`remobiles-main p-4 md:p-6 pb-20 max-w-[1600px] mx-auto w-full transition-[margin] duration-300 ${navCollapsed ? 'md:ml-0' : 'md:ml-56'}`}>
        {children}
        <div className="bottom-nav-spacer" aria-hidden="true" data-testid="bottom-nav-spacer" />
      </main>

      {navCollapsed && (
        <button
          type="button"
          onClick={toggleNavCollapsed}
          className="hidden md:flex fixed left-2 top-[4.5rem] z-40 items-center justify-center w-10 h-10 rounded-xl border border-white/10 bg-[#151820] text-gray-300 hover:text-white hover:border-white/20 shadow-lg"
          aria-label="Afficher le menu"
          data-testid="sidebar-expand-fab"
        >
          <PanelLeftOpen className="w-5 h-5" />
        </button>
      )}

      <nav className="dashboard-bottom-nav md:hidden" data-testid="dashboard-bottom-nav">
        <Link to={homeRoute} data-testid="bottom-nav-dashboard" className={isActive(homeRoute) ? 'is-active' : ''} style={isActive(homeRoute) ? { color: 'var(--rm-red-light)' } : {}}>
          <LayoutDashboard className="w-5 h-5" />
          <span>Accueil</span>
        </Link>
        {!isTech && (
          <Link to="/pos" data-testid="bottom-nav-pos" style={isActive('/pos') ? { color: 'var(--rm-red-light)' } : {}}>
            <ShoppingCart className="w-5 h-5" />
            <span>Vente</span>
          </Link>
        )}
        <Link to="/reparations/nouveau" data-testid="bottom-nav-ajouter" style={isActive('/reparations/nouveau') ? { color: 'var(--rm-red-light)' } : {}}>
          <PlusCircle className="w-5 h-5" />
          <span>Ajouter</span>
        </Link>
        <Link to="/reparations" data-testid="bottom-nav-reparations" style={isActive('/reparations') ? { color: 'var(--rm-red-light)' } : {}}>
          <Smartphone className="w-5 h-5" />
          <span>Repar.</span>
        </Link>
        <button type="button" onClick={() => setPlusOpen(true)} data-testid="bottom-nav-more" className="text-[var(--rm-gray-400)]">
          <Menu className="w-5 h-5" />
          <span>Plus</span>
        </button>
      </nav>

      <ChangePasswordModal isOpen={showChangePassword} onClose={() => setShowChangePassword(false)} accentColor={SITE.accent} />
      <CaisseFermetureReminder />
      <ChatSidebar />
    </div>
  );
};

export default Layout;
